import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { BRANCHES } from './SkillTree/data';
import './SkillCategoryPicker.css';

/**
 * Category picker for a teaching moment. A category can only be a node of the skill tree (a branch, a
 * sub-category, a skill group or a single skill): the coach either types a name and taps a match, or opens the
 * tree and taps the node they want. Works the same for the first assignment and for changing it later.
 *
 * The saved name uses the same format as the skill-tree themes ("Defense › Direct › Forehand › Flat"): the
 * node's path joined with " › ", with the top-level "Technique" branch left out (see backend utils/skillCategories.js).
 */
const nodeName = (path) => (path[0] === 'Technique' && path.length > 1 ? path.slice(1) : path).join(' › ');

// every node of the tree once: { id, title, path, name, depth, children }
const flatten = () => {
  const out = [];
  const walk = (node, parentPath, depth) => {
    const path = [...parentPath, node.title];
    out.push({ id: node.id, title: node.title, path, name: nodeName(path), depth, hasChildren: !!(node.children && node.children.length), node });
    (node.children || []).forEach(c => walk(c, path, depth + 1));
  };
  BRANCHES.forEach(b => walk(b, [], 0));
  return out;
};

const SkillCategoryPicker = ({ currentCategory = null, onSelect, buttonLabel = 'Change category' }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState(() => new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const all = useMemo(flatten, []);

  const matches = useMemo(() => {
    const words = query.toLowerCase().split(/[\s›>]+/).filter(Boolean);
    if (words.length === 0) return [];
    return all.filter(n => words.every(w => n.name.toLowerCase().includes(w))).slice(0, 40);
  }, [all, query]);

  const close = () => { if (saving) return; setOpen(false); setQuery(''); setError(null); };

  const choose = async (name) => {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      await onSelect(name);
      setOpen(false);
      setQuery('');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save the category. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const toggle = (id) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const submit = (e) => {
    e.preventDefault();
    if (matches.length === 1) choose(matches[0].name);
    else {
      const exact = matches.find(m => m.name.toLowerCase() === query.trim().toLowerCase().replace(/\s*>\s*/g, ' › '));
      if (exact) choose(exact.name);
      else setError(matches.length ? 'Tap one of the matching skills below.' : 'That is not in the skill tree. Try another word or browse the tree.');
    }
  };

  const renderTree = (nodes, depth) => nodes.map(node => {
    const path = all.find(n => n.id === node.id);
    const isOpen = expanded.has(node.id);
    const hasKids = node.children && node.children.length > 0;
    const isCurrent = path && path.name === currentCategory;
    return (
      <div key={node.id}>
        <div className={`scp-row${isCurrent ? ' is-current' : ''}`} style={{ paddingLeft: 6 + depth * 14 }}>
          {hasKids ? (
            <button type="button" className={`scp-chev${isOpen ? ' is-open' : ''}`} onClick={() => toggle(node.id)} aria-label={isOpen ? `Collapse ${node.title}` : `Expand ${node.title}`} aria-expanded={isOpen}>▸</button>
          ) : <span className="scp-chev scp-chev--leaf" aria-hidden="true">•</span>}
          <button type="button" className="scp-pick" onClick={() => choose(path.name)} disabled={saving}>
            <span className="scp-pick-title">{node.title}</span>
            {isCurrent && <span className="scp-current-tag">current</span>}
          </button>
        </div>
        {hasKids && isOpen && renderTree(node.children, depth + 1)}
      </div>
    );
  });

  return (
    <>
      <button type="button" className="theme-label-toggle" onClick={() => setOpen(true)}>{buttonLabel}</button>
      {open && createPortal(
        <div className="scp-overlay" onClick={close}>
          <div className="scp-card" role="dialog" aria-label="Choose a skill category" onClick={(e) => e.stopPropagation()}>
            <div className="scp-top">
              <div>
                <h3 className="scp-title">Choose a skill category</h3>
                <p className="scp-sub">{currentCategory ? <>Now: <strong>{currentCategory}</strong></> : 'Only skills from the skill tree can be used.'}</p>
              </div>
              <button type="button" className="scp-x" onClick={close} aria-label="Close" disabled={saving}>✕</button>
            </div>

            <form onSubmit={submit} className="scp-search">
              <input
                className="scp-input"
                value={query}
                onChange={(e) => { setQuery(e.target.value); setError(null); }}
                placeholder="Type a skill, e.g. bandeja or lob"
                aria-label="Search the skill tree"
                autoFocus
                disabled={saving}
              />
              {query && <button type="button" className="scp-clear" onClick={() => setQuery('')} aria-label="Clear">✕</button>}
            </form>
            {error && <div className="scp-error" role="alert">{error}</div>}

            <div className="scp-scroll">
              {query.trim() ? (
                matches.length === 0 ? (
                  <div className="scp-empty">Nothing in the skill tree matches “{query.trim()}”.</div>
                ) : matches.map(m => (
                  <button key={m.id} type="button" className={`scp-match${m.name === currentCategory ? ' is-current' : ''}`} onClick={() => choose(m.name)} disabled={saving}>
                    <span className="scp-match-title">{m.title}</span>
                    <span className="scp-match-path">{m.path.slice(0, -1).join(' › ') || 'Skill tree'}</span>
                  </button>
                ))
              ) : (
                <>
                  <div className="scp-hint">Tap the arrow to open a part of the tree, tap a name to choose it.</div>
                  {renderTree(BRANCHES, 0)}
                </>
              )}
            </div>
            {saving && <div className="scp-saving">Saving…</div>}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default SkillCategoryPicker;
